import { RootState } from "@/redux/store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { extractUmrahFilterData } from "../lib/extract-umrah-filter-data";
import {
  buildUmrahFilterState,
  clampUmrahPriceRange,
} from "../lib/filter-umrah-result";
import {
  IGetUmrahList,
  IUmrahFilterOptionData,
  IUmrahFilterState,
  IUmrahSearchQueryParams,
} from "../types/umrah-search-type";

interface IUmrahFilterSliceState {
  hasValue: boolean;
  defaultOption: IUmrahFilterOptionData;
  selectedOption: IUmrahFilterState;
}

type ISetUmrahFilterSourcePayload = {
  results: IGetUmrahList[];
  searchParams: IUmrahSearchQueryParams;
  keepFilters?: boolean;
};

function createEmptyDefaultOption(): IUmrahFilterOptionData {
  return {
    minPrice: 0,
    maxPrice: 0,
    durations: [],
    groupSizes: [],
  };
}

function createDefaultSelectedOption(
  defaultOption: IUmrahFilterOptionData,
): IUmrahFilterState {
  return {
    query: "",
    minPrice: defaultOption.minPrice,
    maxPrice: defaultOption.maxPrice,
    durations: [],
    groupSizes: [],
  };
}

function hasActiveUmrahFilter(
  selectedOption: IUmrahFilterState,
  defaultOption: IUmrahFilterOptionData,
) {
  if (selectedOption.query.trim()) {
    return true;
  }

  if (selectedOption.durations.length > 0) {
    return true;
  }

  if (selectedOption.groupSizes.length > 0) {
    return true;
  }

  const min = selectedOption.minPrice ?? defaultOption.minPrice;
  const max = selectedOption.maxPrice ?? defaultOption.maxPrice;

  return min !== defaultOption.minPrice || max !== defaultOption.maxPrice;
}

const initialDefaultOption = createEmptyDefaultOption();
const initialState: IUmrahFilterSliceState = {
  hasValue: false,
  defaultOption: initialDefaultOption,
  selectedOption: createDefaultSelectedOption(initialDefaultOption),
};

const extractUmrahFilter = createSlice({
  name: "umrah_filter",
  initialState,
  reducers: {
    setUmrahForExtractFilter: (
      state,
      action: PayloadAction<ISetUmrahFilterSourcePayload>,
    ) => {
      const { results, searchParams, keepFilters = false } = action.payload;
      const defaultOption = extractUmrahFilterData(results);
      state.defaultOption = defaultOption;

      const fromSearch = clampUmrahPriceRange(
        buildUmrahFilterState(searchParams, defaultOption),
        defaultOption,
      );

      if (!keepFilters || !state.hasValue) {
        state.selectedOption = fromSearch;
      } else {
        state.selectedOption = clampUmrahPriceRange(
          state.selectedOption,
          defaultOption,
        );
      }

      state.hasValue = hasActiveUmrahFilter(
        state.selectedOption,
        state.defaultOption,
      );
    },
    setUmrahFilters: (
      state,
      action: PayloadAction<Partial<IUmrahFilterState>>,
    ) => {
      state.selectedOption = clampUmrahPriceRange(
        {
          ...state.selectedOption,
          ...action.payload,
        },
        state.defaultOption,
      );

      state.hasValue = hasActiveUmrahFilter(
        state.selectedOption,
        state.defaultOption,
      );
    },
    clearAllUmrahFilters: (state) => {
      state.selectedOption = createDefaultSelectedOption(state.defaultOption);
      state.hasValue = false;
    },
    removeUmrahFilterItem: (
      state,
      action: PayloadAction<{
        key: keyof IUmrahFilterState;
        value?: string;
      }>,
    ) => {
      const { key, value } = action.payload;
      const current = state.selectedOption[key];

      if (Array.isArray(current)) {
        state.selectedOption[key] = current.filter(
          (item) => item !== value,
        ) as never;
      } else if (key === "query") {
        state.selectedOption.query = "";
      } else if (key === "minPrice") {
        state.selectedOption.minPrice = state.defaultOption.minPrice;
      } else if (key === "maxPrice") {
        state.selectedOption.maxPrice = state.defaultOption.maxPrice;
      }

      state.selectedOption = clampUmrahPriceRange(
        state.selectedOption,
        state.defaultOption,
      );
      state.hasValue = hasActiveUmrahFilter(
        state.selectedOption,
        state.defaultOption,
      );
    },
  },
});

export const {
  setUmrahForExtractFilter,
  setUmrahFilters,
  clearAllUmrahFilters,
  removeUmrahFilterItem,
} = extractUmrahFilter.actions;

export const useUmrahFilter = (state: RootState) => state.extractUmrahFilter;

export default extractUmrahFilter;
